/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY ---
 * - ADDED (Debug Logging): A `console.log` has been added to verify that
 * this updated version of the file is running. It will print the state
 * parameter to the browser console just before the login window opens.
 * The correct state should have three parts (e.g., "google:whitelist:uuid").
 * - MAINTAINED: The core logic of accepting a `listType` and encoding it
 * into the state parameter remains the same.
 */
import { useMemo, useCallback } from 'react';
import { SocialAuthContext } from '../hooks/useSocialAuth';

export const SocialAuthProvider = ({ children }) => {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const discordClientId = import.meta.env.VITE_DISCORD_CLIENT_ID;

  const isSocialAuthReady = !!(googleClientId && discordClientId);

  const handleLogin = useCallback((provider, listType) => {
    if (!isSocialAuthReady) {
      console.error("Social auth is not ready. Client IDs are missing from the .env file.");
      return;
    }

    if (!listType) {
      console.error("Login call is missing 'listType'. Must be 'whitelist', 'newsletter', or 'earn-whitelist'.");
      return;
    }

    const lowerProvider = provider.toLowerCase();
    let config;

    switch (lowerProvider) {
      case 'google':
        config = {
          clientId: googleClientId,
          authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
          scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        };
        break;
      case 'discord':
        config = {
          clientId: discordClientId,
          authUrl: 'https://discord.com/api/oauth2/authorize',
          scope: 'identify email',
        };
        break;
      case 'microsoft':
        return;
      default:
        console.error(`Invalid provider specified: "${provider}"`);
        return;
    }
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const redirectUri = `${cleanBase}/auth/callback`;

    const state = `${lowerProvider}:${listType}:${crypto.randomUUID()}`;
    
    // --- DEBUGGING: Check the browser console for this message ---
    console.log('--- Social Auth Debug ---');
    console.log('Using updated SocialAuthProvider.jsx file.');
    console.log(`Generated state parameter: "${state}"`);
    console.log('-------------------------');
    
    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: config.scope,
      state: state,
      prompt: 'consent',
    });

    const authUrl = `${config.authUrl}?${params.toString()}`;

    window.open(authUrl, '_blank', 'noopener,noreferrer');
  }, [isSocialAuthReady, googleClientId, discordClientId]);

  const value = useMemo(() => ({
    handleLogin,
    isSocialAuthReady
  }), [handleLogin, isSocialAuthReady]);

  return (
    <SocialAuthContext.Provider value={value}>
      {children}
    </SocialAuthContext.Provider>
  );
};
